import { useState, useCallback, useRef, DragEvent, ChangeEvent } from 'react'
import { slugify } from '../../utils/htmlUtils'

// ── Types ──────────────────────────────────────────────────────────────────────

export interface ExtractedPostMeta {
  /** Full HTML document string */
  rawHtmlFile: string
  /** post:title meta value */
  title?: string
  /** post:slug meta value (auto-slugified from title if absent) */
  slug?: string
  /** post:summary meta value */
  summary?: string
  /** post:author meta value */
  author?: string
  /** post:publishedAt meta value (ISO 8601) */
  publishedAt?: string
  /** post:tags meta value, comma-split into array */
  tags?: string[]
  /** post:category meta value */
  category?: string
  /** post:status meta value */
  status?: 'draft' | 'published'
  /** post:featured meta value */
  featured?: boolean
  /** post:id meta value (UUID) */
  id?: string
  /** post:minread meta value — reading time in minutes */
  readingTimeMin?: number
}

interface FileUploadEditorProps {
  currentFile?: string
  onFileLoaded: (extracted: ExtractedPostMeta) => void
  onClear: () => void
}

// ── Meta parser ────────────────────────────────────────────────────────────────

function parseMetaTags(htmlString: string): ExtractedPostMeta {
  // Parse using DOMParser so we don't execute any scripts
  const parser  = new DOMParser()
  const doc     = parser.parseFromString(htmlString, 'text/html')
  const getMeta = (name: string) =>
    doc.querySelector(`meta[name="${name}"]`)?.getAttribute('content') ?? undefined

  const title          = getMeta('post:title')
  const rawSlug        = getMeta('post:slug')
  const slug           = rawSlug ?? (title ? slugify(title) : undefined)
  const tagsRaw        = getMeta('post:tags')
  const tags           = tagsRaw
    ? tagsRaw.split(',').map((t) => t.trim().toLowerCase()).filter(Boolean)
    : undefined
  const featuredRaw    = getMeta('post:featured')
  const featured       = featuredRaw === 'true' ? true : featuredRaw === 'false' ? false : undefined
  const statusRaw      = getMeta('post:status') as 'draft' | 'published' | undefined
  const minReadRaw     = getMeta('post:minread')
  const readingTimeMin = minReadRaw ? Math.max(1, parseInt(minReadRaw, 10) || 1) : undefined

  return {
    rawHtmlFile: htmlString,
    title,
    slug,
    summary:      getMeta('post:summary'),
    author:       getMeta('post:author'),
    publishedAt:  getMeta('post:publishedAt'),
    tags,
    category:     getMeta('post:category'),
    status:       statusRaw === 'published' ? 'published' : statusRaw === 'draft' ? 'draft' : undefined,
    featured,
    id:           getMeta('post:id'),
    readingTimeMin,
  }
}

// ── Component ──────────────────────────────────────────────────────────────────

export function FileUploadEditor({ currentFile, onFileLoaded, onClear }: FileUploadEditorProps) {
  const [dragging, setDragging]   = useState(false)
  const [fileName, setFileName]   = useState<string | null>(null)
  const [preview,  setPreview]    = useState<string | null>(null)
  const [error,    setError]      = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleFile = useCallback((file: File) => {
    setError(null)
    if (!file.name.endsWith('.html') && file.type !== 'text/html') {
      setError('Only .html files are accepted.')
      return
    }
    const reader = new FileReader()
    reader.onload = (ev) => {
      const content = ev.target?.result as string
      if (!content) { setError('File appears to be empty.'); return }
      const extracted = parseMetaTags(content)
      setFileName(file.name)
      // Show a brief preview: first 200 chars of body text
      const parser = new DOMParser()
      const doc    = parser.parseFromString(content, 'text/html')
      const text   = (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim().slice(0, 200)
      setPreview(text)
      onFileLoaded(extracted)
    }
    reader.onerror = () => setError('Failed to read file.')
    reader.readAsText(file, 'utf-8')
  }, [onFileLoaded])

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragging(false)
    const file = e.dataTransfer.files[0]
    if (file) handleFile(file)
  }

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleFile(file)
    e.target.value = '' // allow re-selecting same file
  }

  const handleClear = () => {
    setFileName(null)
    setPreview(null)
    setError(null)
    onClear()
  }

  const hasFile = !!currentFile

  return (
    <>
      <div className="fue-wrap">
        {/* Drop zone */}
        {!hasFile && (
          <div
            className={`fue-dropzone ${dragging ? 'fue-dropzone--active' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.click()}
            aria-label="Upload HTML file"
          >
            <div className="fue-icon">⬆</div>
            <p className="fue-label">Drop your <code>.html</code> post file here</p>
            <p className="fue-sub">or click to browse</p>
            <p className="fue-sub">Metadata is auto-extracted from <code>&lt;meta name="post:*"&gt;</code> tags</p>
            <input
              ref={inputRef}
              type="file"
              accept=".html,text/html"
              className="fue-hidden-input"
              onChange={onInputChange}
              aria-hidden
            />
          </div>
        )}

        {/* Loaded state */}
        {hasFile && fileName && (
          <div className="fue-loaded">
            <div className="fue-loaded-header">
              <div className="fue-loaded-info">
                <span className="fue-file-icon">🗎</span>
                <div>
                  <p className="fue-file-name">{fileName}</p>
                  <p className="fue-file-size">
                    {(currentFile!.length / 1024).toFixed(1)} KB · full HTML document
                  </p>
                </div>
              </div>
              <button className="fue-clear-btn" onClick={handleClear} type="button">
                Replace file
              </button>
            </div>

            {preview && (
              <div className="fue-preview">
                <span className="fue-preview-label">Preview text</span>
                <p className="fue-preview-text">{preview}…</p>
              </div>
            )}

            <div className="fue-meta-note">
              <span className="fue-meta-note-icon">✓</span>
              Metadata was auto-extracted from the file's <code>&lt;meta&gt;</code> tags and
              pre-filled in the form fields above. Review and adjust as needed before saving.
            </div>
          </div>
        )}

        {/* Re-upload zone when file loaded but fileName not set yet (e.g. editing existing) */}
        {hasFile && !fileName && (
          <div className="fue-loaded">
            <div className="fue-loaded-header">
              <div className="fue-loaded-info">
                <span className="fue-file-icon">🗎</span>
                <div>
                  <p className="fue-file-name">Existing uploaded HTML file</p>
                  <p className="fue-file-size">
                    {(currentFile!.length / 1024).toFixed(1)} KB
                  </p>
                </div>
              </div>
              <button className="fue-clear-btn" onClick={handleClear} type="button">
                Remove / use editor
              </button>
            </div>
            <div className="fue-meta-note">
              This post was originally published from an uploaded HTML file.
              Click "Remove / use editor" to switch to the WYSIWYG editor.
            </div>
          </div>
        )}

        {error && <p className="fue-error">{error}</p>}
      </div>

      <style>{`
        .fue-wrap { display: flex; flex-direction: column; gap: 12px; }

        /* Drop zone */
        .fue-dropzone {
          border: 1.5px dashed var(--border);
          border-radius: var(--radius-lg);
          padding: 48px 32px;
          text-align: center;
          cursor: pointer;
          transition: border-color 0.15s, background 0.15s;
          background: var(--bg-input);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          outline: none;
        }
        .fue-dropzone:hover,
        .fue-dropzone:focus-visible { border-color: var(--accent); background: var(--accent-subtle); }
        .fue-dropzone--active { border-color: var(--accent); background: var(--accent-subtle); }
        .fue-icon { font-size: 2rem; margin-bottom: 4px; }
        .fue-label { font-size: 0.95rem; font-weight: 600; color: var(--text-primary); }
        .fue-label code, .fue-sub code {
          font-family: var(--font-mono);
          font-size: 0.85em;
          color: var(--color-info);
          background: rgba(56,189,248,0.1);
          padding: 1px 5px;
          border-radius: 3px;
        }
        .fue-sub { font-size: 0.8rem; color: var(--text-muted); }
        .fue-hidden-input { display: none; }

        /* Loaded card */
        .fue-loaded {
          border: 1px solid var(--border);
          border-radius: var(--radius-lg);
          overflow: hidden;
        }
        .fue-loaded-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 18px;
          background: var(--bg-surface);
          border-bottom: 1px solid var(--border);
          gap: 12px;
          flex-wrap: wrap;
        }
        .fue-loaded-info { display: flex; align-items: center; gap: 12px; }
        .fue-file-icon { font-size: 1.6rem; }
        .fue-file-name {
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--text-primary);
          font-family: var(--font-mono);
        }
        .fue-file-size { font-size: 0.75rem; color: var(--text-muted); margin-top: 2px; }
        .fue-clear-btn {
          font-size: 0.78rem;
          font-weight: 500;
          color: var(--text-muted);
          padding: 6px 12px;
          border: 1px solid var(--border);
          border-radius: var(--radius-sm);
          transition: color 0.12s, border-color 0.12s;
          flex-shrink: 0;
        }
        .fue-clear-btn:hover { color: var(--color-error); border-color: var(--color-error); }

        /* Preview */
        .fue-preview {
          padding: 12px 18px;
          border-bottom: 1px solid var(--border);
          background: var(--bg-input);
        }
        .fue-preview-label {
          display: block;
          font-size: 0.7rem;
          font-weight: 700;
          color: var(--text-muted);
          text-transform: uppercase;
          letter-spacing: 0.06em;
          margin-bottom: 5px;
        }
        .fue-preview-text {
          font-size: 0.82rem;
          color: var(--text-secondary);
          line-height: 1.6;
          font-style: italic;
        }

        /* Meta note */
        .fue-meta-note {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          padding: 12px 18px;
          font-size: 0.8rem;
          color: var(--text-muted);
          line-height: 1.5;
        }
        .fue-meta-note-icon { color: var(--color-success); font-weight: 700; flex-shrink: 0; }
        .fue-meta-note code {
          font-family: var(--font-mono);
          font-size: 0.82em;
          color: var(--color-info);
        }

        /* Error */
        .fue-error {
          font-size: 0.8rem;
          color: var(--color-error);
          padding: 8px 12px;
          background: var(--bg-error);
          border-radius: var(--radius-md);
          border: 1px solid var(--color-error);
        }
      `}</style>
    </>
  )
}
